import { useEffect, useRef, useState } from "react";
import {
  LocalStageStream,
  Stage,
  StageConnectionState,
  StageEvents,
  SubscribeType,
} from "amazon-ivs-web-broadcast";
import { QRCodeSVG } from "qrcode.react";
import "./App.css";

const START_BROADCAST_URL = "https://tabapv.site/StartLiveBroadcast";
const FORCE_STOP_BROADCAST_URL = "https://tabapv.site/forceStopLiveBroadcast";
const CHAT_SYNC_EVENT = "alivebroadcast-chat-sync";
const DEFAULT_CHAT_API_BASE = "https://lvap.lyvoeg.com";

function pickFirstString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
}

function getStageIdFromArn(stageArn) {
  const normalizedArn = pickFirstString(stageArn);

  if (!normalizedArn) {
    return "";
  }

  const parts = normalizedArn.split("/");
  return parts[parts.length - 1] || normalizedArn;
}

function normalizeRealTimeSession(source = {}) {
  const normalizedSource = source || {};
  const stageArn = pickFirstString(
    normalizedSource.stage_arn,
    normalizedSource.stageArn,
    normalizedSource.arn,
    normalizedSource.channel_arn,
    normalizedSource.channelArn,
  );
  const broadcasterToken = pickFirstString(
    normalizedSource.broadcaster_token,
    normalizedSource.broadcasterToken,
    normalizedSource.host_token,
    normalizedSource.hostToken,
    normalizedSource.publisher_token,
    normalizedSource.publisherToken,
    normalizedSource.participant_token,
    normalizedSource.participantToken,
    normalizedSource.stage_token,
    normalizedSource.stageToken,
    normalizedSource.token,
  );
  const viewerToken = pickFirstString(
    normalizedSource.viewer_token,
    normalizedSource.viewerToken,
  );
  const viewerJoinUrl = pickFirstString(
    normalizedSource.viewer_join_url,
    normalizedSource.viewerJoinUrl,
    normalizedSource.viewer_token_endpoint,
    normalizedSource.viewerTokenEndpoint,
    normalizedSource.join_url,
    normalizedSource.joinUrl,
    normalizedSource.token_endpoint,
    normalizedSource.tokenEndpoint,
  );
  const shareUrl = pickFirstString(
    normalizedSource.viewer_url,
    normalizedSource.viewerUrl,
    normalizedSource.share_url,
    normalizedSource.shareUrl,
  );
  const roomId = pickFirstString(
    normalizedSource.room_id,
    normalizedSource.roomId,
    getStageIdFromArn(stageArn),
  );

  return {
    ...normalizedSource,
    stageArn,
    broadcasterToken,
    viewerToken,
    viewerJoinUrl,
    shareUrl,
    roomId,
  };
}

function getPresenceClientId(role) {
  const storageKey = `alivebroadcast-presence:${role}`;

  try {
    const existing = window.sessionStorage.getItem(storageKey);

    if (existing) {
      return existing;
    }

    const nextValue = `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem(storageKey, nextValue);
    return nextValue;
  } catch (error) {
    return `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

function getChatApiBase() {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("chatApi") ||
    process.env.REACT_APP_CHAT_API_BASE ||
    DEFAULT_CHAT_API_BASE
  );
}

function formatLabel(value) {
  if (!value) {
    return "Not available yet";
  }

  return value;
}

function getViewerParams() {
  const params = new URLSearchParams(window.location.search);
  let isFramed = false;
  const hideEverything =
    params.get("hideeverything") === "true" ||
    params.get("hideEverything") === "true";
  const portraitMode =
    params.get("protrait") === "true" || params.get("portrait") === "true";
  const instId =
    params.get("intid") || params.get("instid") || params.get("instId") || "";
  const userId =
    params.get("usid") || params.get("userid") || params.get("userId") || "";

  try {
    isFramed = window.self !== window.top;
  } catch (error) {
    isFramed = true;
  }

  return {
    role: params.get("role") || "broadcaster",
    stageArn: pickFirstString(
      params.get("stageArn"),
      params.get("stagearn"),
      params.get("channel"),
    ),
    viewerToken: pickFirstString(
      params.get("viewerToken"),
      params.get("viewer_token"),
      params.get("token"),
      params.get("participantToken"),
      params.get("participant_token"),
      params.get("stageToken"),
      params.get("stage_token"),
    ),
    viewerJoinUrl: pickFirstString(
      params.get("viewerJoinUrl"),
      params.get("viewer_join_url"),
      params.get("viewerTokenEndpoint"),
      params.get("viewer_token_endpoint"),
      params.get("joinUrl"),
      params.get("join_url"),
      params.get("tokenEndpoint"),
      params.get("token_endpoint"),
    ),
    roomId: pickFirstString(params.get("roomId"), params.get("room_id")),
    embed:
      params.get("embed") === "1" || params.get("embed") === "true" || isFramed,
    hideEverything,
    portraitMode,
    chatApi:
      params.get("chatApi") ||
      process.env.REACT_APP_CHAT_API_BASE ||
      DEFAULT_CHAT_API_BASE,
    instId,
    userId,
  };
}

function buildViewerLink(streamDetails) {
  const session = normalizeRealTimeSession(streamDetails);

  if (session.shareUrl) {
    const sharedUrl = new URL(session.shareUrl, window.location.origin);
    sharedUrl.searchParams.set("role", "viewer");
    sharedUrl.searchParams.set("embed", "1");

    if (session.stageArn) {
      sharedUrl.searchParams.set("stageArn", session.stageArn);
    }

    if (session.viewerJoinUrl) {
      sharedUrl.searchParams.set("viewerJoinUrl", session.viewerJoinUrl);
    }

    if (session.viewerToken) {
      sharedUrl.searchParams.set("viewerToken", session.viewerToken);
    }

    if (session.roomId) {
      sharedUrl.searchParams.set("roomId", session.roomId);
    }

    const chatApiBase = getChatApiBase();
    const viewerParams = getViewerParams();

    if (viewerParams.instId) {
      sharedUrl.searchParams.set("intid", viewerParams.instId);
    }

    if (viewerParams.userId) {
      sharedUrl.searchParams.set("usid", viewerParams.userId);
    }

    if (chatApiBase) {
      sharedUrl.searchParams.set("chatApi", chatApiBase);
    }

    return sharedUrl.toString();
  }

  if (!session.stageArn && !session.viewerJoinUrl && !session.viewerToken) {
    return "";
  }

  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("role", "viewer");
  url.searchParams.set("embed", "1");
  const chatApiBase = getChatApiBase();
  const viewerParams = getViewerParams();

  if (session.stageArn) {
    url.searchParams.set("stageArn", session.stageArn);
  }

  if (session.viewerJoinUrl) {
    url.searchParams.set("viewerJoinUrl", session.viewerJoinUrl);
  }

  if (session.viewerToken) {
    url.searchParams.set("viewerToken", session.viewerToken);
  }

  if (session.roomId) {
    url.searchParams.set("roomId", session.roomId);
  }

  if (viewerParams.instId) {
    url.searchParams.set("intid", viewerParams.instId);
  }

  if (viewerParams.userId) {
    url.searchParams.set("usid", viewerParams.userId);
  }

  if (chatApiBase) {
    url.searchParams.set("chatApi", chatApiBase);
  }

  return url.toString();
}

function buildChatRoomId(...values) {
  return pickFirstString(...values) || "default-room";
}

async function resolveViewerParticipantToken({
  viewerToken,
  viewerJoinUrl,
  stageArn,
  instId,
  userId,
}) {
  if (viewerToken) {
    return viewerToken;
  }

  if (!viewerJoinUrl) {
    throw new Error(
      "This real-time viewer link is missing a participant token or viewer join endpoint.",
    );
  }

  const tryReadToken = async (responsePromise) => {
    const response = await responsePromise;

    if (!response.ok) {
      throw new Error(`Viewer join failed with status ${response.status}.`);
    }

    const payload = await response.json();
    const token = pickFirstString(
      payload.viewer_token,
      payload.viewerToken,
      payload.participant_token,
      payload.participantToken,
      payload.stage_token,
      payload.stageToken,
      payload.token,
    );

    if (!token) {
      throw new Error("Viewer join response did not include a stage token.");
    }

    return token;
  };

  try {
    return await tryReadToken(
      fetch(viewerJoinUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          intid: instId,
          usid: userId,
          instid: instId,
          userid: userId,
          stageArn,
          role: "viewer",
        }),
      }),
    );
  } catch (postError) {
    const url = new URL(viewerJoinUrl, window.location.origin);

    if (instId) {
      url.searchParams.set("intid", instId);
    }

    if (userId) {
      url.searchParams.set("usid", userId);
    }

    if (stageArn) {
      url.searchParams.set("stageArn", stageArn);
    }

    url.searchParams.set("role", "viewer");

    return tryReadToken(fetch(url.toString()));
  }
}

function buildChatStorageKey(roomId) {
  return `alivebroadcast-chat:${roomId}`;
}

function readChatMessages(roomId) {
  if (!roomId) {
    return [];
  }

  try {
    const savedValue = window.localStorage.getItem(buildChatStorageKey(roomId));
    return savedValue ? JSON.parse(savedValue) : [];
  } catch (error) {
    return [];
  }
}

function writeChatMessages(roomId, messages) {
  if (!roomId) {
    return;
  }

  window.localStorage.setItem(
    buildChatStorageKey(roomId),
    JSON.stringify(messages),
  );
  window.dispatchEvent(
    new CustomEvent(CHAT_SYNC_EVENT, {
      detail: { roomId, messages },
    }),
  );
}

function useChatRoom({ roomId, role, apiBase }) {
  const [messages, setMessages] = useState(() => readChatMessages(roomId));
  const [transport, setTransport] = useState(apiBase ? "connecting" : "local");

  useEffect(() => {
    setMessages(readChatMessages(roomId));
  }, [roomId]);

  useEffect(() => {
    if (!apiBase || !roomId) {
      setTransport("local");
      return undefined;
    }

    let isMounted = true;

    const fetchRemoteMessages = async () => {
      try {
        const response = await fetch(
          `${apiBase.replace(/\/$/, "")}/chat/messages?roomId=${encodeURIComponent(roomId)}`,
        );

        if (!response.ok) {
          throw new Error("Chat API unavailable");
        }

        const payload = await response.json();

        if (!isMounted) {
          return;
        }

        setMessages(Array.isArray(payload.messages) ? payload.messages : []);
        setTransport("remote");
      } catch (error) {
        if (isMounted) {
          setTransport("local");
        }
      }
    };

    void fetchRemoteMessages();
    const intervalId = window.setInterval(fetchRemoteMessages, 2500);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [apiBase, roomId]);

  useEffect(() => {
    if (transport !== "local") {
      return undefined;
    }

    const syncMessages = (nextRoomId) => {
      if (nextRoomId === roomId) {
        setMessages(readChatMessages(roomId));
      }
    };

    const handleStorage = (event) => {
      if (event.key === buildChatStorageKey(roomId)) {
        setMessages(readChatMessages(roomId));
      }
    };

    const handleCustomSync = (event) => {
      syncMessages(event.detail?.roomId);
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(CHAT_SYNC_EVENT, handleCustomSync);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(CHAT_SYNC_EVENT, handleCustomSync);
    };
  }, [roomId, transport]);

  const sendMessage = async (body) => {
    const trimmedBody = body.trim();

    if (!trimmedBody || !roomId) {
      return;
    }

    if (apiBase) {
      try {
        const response = await fetch(
          `${apiBase.replace(/\/$/, "")}/chat/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              roomId,
              role,
              body: trimmedBody,
            }),
          },
        );

        if (response.ok) {
          const payload = await response.json();
          if (Array.isArray(payload.messages)) {
            setMessages(payload.messages);
            setTransport("remote");
            return;
          }
        }
      } catch (error) {
        setTransport("local");
      }
    }

    const nextMessages = [
      ...readChatMessages(roomId),
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role,
        body: trimmedBody,
        createdAt: new Date().toISOString(),
      },
    ];

    setMessages(nextMessages);
    writeChatMessages(roomId, nextMessages);
  };

  return { messages, sendMessage, transport };
}

function formatMessageTime(value) {
  try {
    return new Intl.DateTimeFormat("en", {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return "";
  }
}

function usePresence({ roomId, role, apiBase }) {
  const [viewerCount, setViewerCount] = useState(0);
  const [presenceReady, setPresenceReady] = useState(false);
  const clientIdRef = useRef(null);

  useEffect(() => {
    if (!clientIdRef.current) {
      clientIdRef.current = getPresenceClientId(role);
    }
  }, [role]);

  useEffect(() => {
    if (!apiBase || !roomId || !clientIdRef.current) {
      setPresenceReady(false);
      setViewerCount(0);
      return undefined;
    }

    let isMounted = true;
    const normalizedApiBase = apiBase.replace(/\/$/, "");

    const heartbeat = async () => {
      try {
        const response = await fetch(
          `${normalizedApiBase}/presence/heartbeat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              roomId,
              role,
              clientId: clientIdRef.current,
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Presence API unavailable");
        }

        const payload = await response.json();

        if (isMounted) {
          setViewerCount(Number(payload.viewerCount) || 0);
          setPresenceReady(true);
        }
      } catch (error) {
        if (isMounted) {
          setPresenceReady(false);
        }
      }
    };

    void heartbeat();
    const intervalId = window.setInterval(heartbeat, 10000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [apiBase, roomId, role]);

  return { viewerCount, presenceReady };
}

function MissingParamsOverlay() {
  return (
    <div className="fullscreen-overlay">
      <div className="fullscreen-overlay-card">
        <p className="eyebrow">Missing Information</p>
        <h1>Required URL parameters are missing.</h1>
        <p className="hero-text">
          Open this page with both <code>intid</code> and <code>usid</code> in
          the URL.
        </p>
        <p className="overlay-example">
          Example: <code>localhost:3000?intid=value&amp;usid=value</code>
        </p>
      </div>
    </div>
  );
}

function ChatRoom({
  roomId,
  role,
  compact = false,
  embed = false,
  overlay = false,
  apiBase = "",
}) {
  const { messages, sendMessage, transport } = useChatRoom({
    roomId,
    role,
    apiBase,
  });
  const [draft, setDraft] = useState("");
  const [collapsed, setCollapsed] = useState(embed || overlay);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const submitMessage = (event) => {
    event.preventDefault();
    if (!draft.trim()) {
      return;
    }

    void sendMessage(draft);
    setDraft("");
  };

  return (
    <section
      className={`chat-card ${compact ? "chat-card-compact" : ""} ${
        embed ? "chat-card-embed" : ""
      } ${overlay ? "chat-card-overlay" : ""} ${
        collapsed ? "chat-card-collapsed" : ""
      }`}
    >
      {overlay ? (
        <button
          type="button"
          className="chat-toggle-button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "Show chat" : "Hide chat"}
        >
          {collapsed ? "\u2039" : "\u203a"}
        </button>
      ) : null}

      <div className="chat-header">
        <div>
          <p className="eyebrow">Live Chat</p>
          <h3>
            {role === "broadcaster"
              ? "Viewer questions"
              : "Ask the broadcaster"}
          </h3>
        </div>
        <span className="chat-count-pill">{messages.length} messages</span>
      </div>

      <p className="chat-note">
        {transport === "remote"
          ? "Connected to the Node.js chat API. Messages can sync across devices."
          : "Using browser-local chat fallback. Add the Node.js chat API URL to sync across devices."}
      </p>

      <div
        className="chat-messages"
        aria-label="chat messages"
        ref={messagesRef}
      >
        {messages.length ? (
          messages.map((message) => (
            <article
              key={message.id}
              className={`chat-message ${
                message.role === role ? "chat-message-own" : "chat-message-peer"
              }`}
            >
              <div className="chat-message-meta">
                <strong>
                  {message.role === "broadcaster" ? "Broadcaster" : "Viewer"}
                </strong>
                <span>{formatMessageTime(message.createdAt)}</span>
              </div>
              <p>{message.body}</p>
            </article>
          ))
        ) : (
          <div className="chat-empty-state">
            No messages yet.{" "}
            {role === "broadcaster"
              ? "Wait for viewer questions."
              : "Ask the first question."}
          </div>
        )}
      </div>

      <form className="chat-form" onSubmit={submitMessage}>
        <textarea
          className="chat-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={
            role === "broadcaster"
              ? "Reply to the live audience..."
              : "Ask something about the live stream..."
          }
          rows={compact ? 2 : 3}
        />
        <button className="primary-button chat-send-button" type="submit">
          Send
        </button>
      </form>
    </section>
  );
}

function BroadcasterScreen({ instId, userId }) {
  const previewRef = useRef(null);
  const stageRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const localStageStreamsRef = useRef([]);
  const [streamDetails, setStreamDetails] = useState(null);
  const [setupState, setSetupState] = useState("idle");
  const [publishState, setPublishState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [connectionState, setConnectionState] = useState("disconnected");
  const [shareStatus, setShareStatus] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [stageViewerCount, setStageViewerCount] = useState(null);
  const [isForceStopping, setIsForceStopping] = useState(false);
  const chatApiBase = getChatApiBase();
  const unlockedChatApiBase = isUnlocked ? chatApiBase : "";

  useEffect(() => {
    document.body.classList.add("live-no-scroll");

    return () => {
      document.body.classList.remove("live-no-scroll");

      if (stageRef.current) {
        stageRef.current.leave();
      }

      stageRef.current = null;

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      mediaStreamRef.current = null;
      localStageStreamsRef.current = [];
    };
  }, []);

  const canStartBroadcast =
    setupState === "ready" &&
    publishState !== "starting" &&
    Boolean(streamDetails?.broadcasterToken);
  const isLive = publishState === "live";
  const missingBroadcastToken =
    streamDetails && !streamDetails.broadcasterToken;
  const shareableLink = buildViewerLink(streamDetails);
  const chatRoomId = buildChatRoomId(
    streamDetails?.roomId,
    streamDetails?.stageArn,
  );
  const { viewerCount, presenceReady } = usePresence({
    roomId: chatRoomId,
    role: "broadcaster",
    apiBase: unlockedChatApiBase,
  });
  const effectiveViewerCount =
    typeof stageViewerCount === "number"
      ? stageViewerCount
      : presenceReady
        ? viewerCount
        : "--";

  const cleanupLocalBroadcastSession = () => {
    if (stageRef.current) {
      stageRef.current.leave();
    }

    stageRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    mediaStreamRef.current = null;
    localStageStreamsRef.current = [];

    if (previewRef.current) {
      previewRef.current.pause?.();
      previewRef.current.srcObject = null;
    }

    setConnectionState(StageConnectionState.DISCONNECTED);
    setPublishState("idle");
    setStageViewerCount(null);
  };

  const initializeBroadcaster = async (event) => {
    event.preventDefault();
    let unlockGranted = false;

    if (setupState === "loading") {
      return;
    }

    if (!password.trim()) {
      setErrorMessage("Password is required before starting the broadcaster.");
      return;
    }
    try {
      setSetupState("loading");
      setErrorMessage("");
      setStageViewerCount(null);

      const response = await fetch(START_BROADCAST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: password.trim(),
          intid: instId,
          usid: userId,
          instid: instId,
          userid: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Start request failed with status ${response.status}.`);
      }

      const payload = await response.json();

      const hasUnlocked =
        payload?.status === true ||
        payload?.status === "true" ||
        payload?.status === 1 ||
        payload?.status === "1";

      if (!hasUnlocked) {
        throw new Error(payload?.message || "Broadcaster unlock failed.");
      }

      unlockGranted = true;
      setIsUnlocked(true);

      const session = normalizeRealTimeSession(payload);

      if (!session.broadcasterToken) {
        const isLowLatencyPayload =
          Boolean(payload?.stream_key) ||
          Boolean(payload?.ingest_endpoint) ||
          Boolean(payload?.playback_url);

        throw new Error(
          isLowLatencyPayload
            ? "The backend is still returning IVS low-latency fields (stream_key / ingest_endpoint / playback_url). Real-time mode needs a stage participant token instead."
            : "The backend did not return a real-time broadcaster token.",
        );
      }

      setStreamDetails(session);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      });

      mediaStreamRef.current = mediaStream;
      localStageStreamsRef.current = mediaStream
        .getTracks()
        .map((track) => new LocalStageStream(track));

      if (previewRef.current) {
        previewRef.current.srcObject = mediaStream;
        previewRef.current.muted = true;
        previewRef.current.playsInline = true;
        previewRef.current.autoplay = true;
        await previewRef.current.play().catch(() => undefined);
      }

      const stage = new Stage(session.broadcasterToken, {
        stageStreamsToPublish() {
          return localStageStreamsRef.current;
        },
        shouldPublishParticipant(participant) {
          return participant.isLocal;
        },
        shouldSubscribeToParticipant() {
          return SubscribeType.NONE;
        },
      });

      stageRef.current = stage;
      stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (nextState) => {
        setConnectionState(nextState);

        if (nextState === StageConnectionState.CONNECTED) {
          setPublishState("live");
        } else if (nextState === StageConnectionState.CONNECTING) {
          setPublishState("starting");
        } else {
          setPublishState("idle");
        }
      });
      stage.on(StageEvents.STAGE_SUBSCRIBER_COUNT_CHANGED, (count) => {
        setStageViewerCount(Number(count) || 0);
      });
      stage.on(StageEvents.ERROR, (error) => {
        setConnectionState(StageConnectionState.ERRORED);
        setPublishState("idle");
        setErrorMessage(error?.message || "Stage connection error.");
      });
      stage.on(StageEvents.STAGE_LEFT, () => {
        setConnectionState(StageConnectionState.DISCONNECTED);
        setPublishState("idle");
      });

      setSetupState("ready");
    } catch (error) {
      if (!unlockGranted) {
        setIsUnlocked(false);
      }
      setSetupState("error");
      setErrorMessage(
        error?.message ||
          "Unable to prepare the broadcaster. Check password, permissions, and endpoint access.",
      );
    }
  };

  const startBroadcast = async () => {
    if (!stageRef.current || !streamDetails?.broadcasterToken) {
      setErrorMessage(
        "A valid real-time broadcaster token is required to go live.",
      );
      return;
    }

    try {
      setPublishState("starting");
      setErrorMessage("");
      await stageRef.current.join();
    } catch (error) {
      setPublishState("idle");
      setErrorMessage(error?.message || "Unable to join the real-time stage.");
    }
  };

  const stopBroadcast = () => {
    cleanupLocalBroadcastSession();
  };

  const forceStopBroadcast = async () => {
    if (isForceStopping) {
      return;
    }

    if (!streamDetails?.stageArn) {
      setErrorMessage("A stage ARN is required to force stop the live.");
      return;
    }

    if (!password.trim()) {
      setErrorMessage(
        "The broadcaster password is required to force stop the live.",
      );
      return;
    }

    try {
      setIsForceStopping(true);
      setErrorMessage("Sending end live request...");

      const response = await fetch(FORCE_STOP_BROADCAST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: password.trim(),
          stageArn: streamDetails.stageArn,
          stage_arn: streamDetails.stageArn,
          arn: streamDetails.stageArn,
          intid: instId,
          usid: userId,
          instid: instId,
          userid: userId,
          userId,
          instId,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Force stop request failed with status ${response.status}.`,
        );
      }

      const payload = await response.json();
      const wasStopped =
        payload?.status === true ||
        payload?.status === "true" ||
        payload?.status === 1 ||
        payload?.status === "1";

      if (!wasStopped) {
        throw new Error(
          payload?.reason || payload?.message || "Force stop failed.",
        );
      }

      cleanupLocalBroadcastSession();
      setErrorMessage("The live has been force stopped.");
    } catch (error) {
      setErrorMessage(error?.message || "Unable to force stop the live.");
    } finally {
      setIsForceStopping(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareableLink) {
      setShareStatus("Viewer link is not available yet.");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareableLink);
      setShareStatus("Viewer link copied.");
    } catch (error) {
      setShareStatus(
        "Could not copy automatically. You can copy the link manually.",
      );
    }
  };

  return (
    <main className="broadcaster-shell">
      <section className="broadcaster-panel">
        <div className="broadcaster-video-card">
          <video
            ref={previewRef}
            className="preview-canvas broadcaster-canvas"
            muted
            autoPlay
            playsInline
          />

          <div className="viewer-overlay broadcaster-overlay">
            <div className="viewer-overlay-top">
              <div className="viewer-top-stack">
                <p className="eyebrow">Lyvo Live</p>
              </div>
              <span className={`viewer-state-pill status-${setupState}`}>
                Setup: {setupState}
              </span>
              <span
                className={`viewer-live-pill ${isLive ? "" : "viewer-live-pill-idle"}`}
              >
                {isLive ? "Live" : "Preview"}
              </span>
              <span className="viewer-state-pill">
                Stage: {isLive ? "joined" : publishState}
              </span>
              <span className="viewer-state-pill">
                Connection: {connectionState}
              </span>
              <span className="viewer-state-pill">
                Viewers online: {effectiveViewerCount}
              </span>
            </div>

            <div className="viewer-overlay-bottom broadcaster-overlay-bottom">
              <div className="viewer-overlay-actions">
                <button
                  className="primary-button"
                  onClick={startBroadcast}
                  disabled={!canStartBroadcast}
                >
                  {publishState === "starting" ? "Joining..." : "Start live"}
                </button>
                <button
                  className="secondary-button"
                  onClick={stopBroadcast}
                  disabled={!isLive && publishState !== "starting"}
                >
                  Stop live
                </button>
                {/* <button className="danger-button" onClick={forceStopBroadcast}> */}
                <button
                  className="secondary-button"
                  // className="danger-button"
                  onClick={forceStopBroadcast}
                >
                  {isForceStopping ? "Ending..." : "End live"}
                </button>
                <a
                  className="secondary-button secondary-link"
                  href={shareableLink || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open viewer screen
                </a>
              </div>
            </div>
          </div>

          {missingBroadcastToken ? (
            <div className="viewer-alert-overlay broadcaster-alert-overlay">
              <p className="warning-banner viewer-error-banner">
                The backend responded, but no real-time broadcaster token was
                returned. The stage cannot be joined until the API sends a valid
                participant token.
              </p>
            </div>
          ) : null}

          {errorMessage ? (
            <div className="viewer-alert-overlay broadcaster-alert-overlay">
              <p className="error-banner viewer-error-banner">{errorMessage}</p>
            </div>
          ) : null}

          <aside
            className={`details-overlay-panel ${detailsOpen ? "details-overlay-open" : ""}`}
          >
            <button
              type="button"
              className="details-toggle-button"
              onClick={() => setDetailsOpen((value) => !value)}
              aria-label={
                detailsOpen ? "Hide session details" : "Show session details"
              }
            >
              {detailsOpen ? "\u203a" : "\u2039"}
            </button>

            <div className="details-overlay-content">
              <div className="chat-header details-overlay-header">
                <div>
                  <p className="eyebrow">Session Details</p>
                  <h3>Session details</h3>
                </div>
              </div>

              <dl className="details-grid details-grid-overlay">
                <div>
                  <dt>Stage ARN</dt>
                  <dd>{formatLabel(streamDetails?.stageArn)}</dd>
                </div>
                <div>
                  <dt>Broadcaster token</dt>
                  <dd>
                    {streamDetails?.broadcasterToken
                      ? "Ready"
                      : "Not available yet"}
                  </dd>
                </div>
                <div>
                  <dt>Viewer join endpoint</dt>
                  <dd className="long-value">
                    {streamDetails?.viewerJoinUrl ? (
                      <a
                        href={streamDetails.viewerJoinUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open endpoint
                      </a>
                    ) : (
                      "Not available yet"
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Room ID</dt>
                  <dd className="long-value">
                    {formatLabel(streamDetails?.roomId)}
                  </dd>
                </div>
                <div>
                  <dt>Shareable viewer link</dt>
                  <dd className="long-value">
                    {shareableLink ? (
                      <>
                        <a
                          href={shareableLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open viewer link
                        </a>
                        <button
                          className="inline-copy-button"
                          onClick={copyShareLink}
                        >
                          Copy
                        </button>
                      </>
                    ) : (
                      "Waiting for stage details"
                    )}
                  </dd>
                  {shareStatus ? (
                    <p className="inline-status">{shareStatus}</p>
                  ) : null}
                </div>
                <div>
                  <dt>Viewer QR code</dt>
                  <dd className="long-value">
                    {shareableLink ? (
                      <div className="qr-code-card">
                        <QRCodeSVG
                          value={shareableLink}
                          size={168}
                          bgColor="#ffffff"
                          fgColor="rgb(163, 29, 28)"
                          includeMargin
                        />
                        <p className="qr-code-note">
                          Scan to open the viewer page directly.
                        </p>
                      </div>
                    ) : (
                      "Waiting for stage details"
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>

          <ChatRoom
            roomId={chatRoomId}
            role="broadcaster"
            compact
            overlay
            apiBase={unlockedChatApiBase}
          />
        </div>
      </section>

      {!isUnlocked ? (
        <div className="modal-backdrop">
          <div
            className="auth-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unlock-title"
          >
            <p className="eyebrow">Lyvo Live</p>
            <h2 id="unlock-title"></h2>

            <form
              className="auth-card auth-card-modal"
              onSubmit={initializeBroadcaster}
            >
              <label className="auth-label" htmlFor="broadcaster-password">
                Broadcaster password
              </label>
              <div className="auth-input-row">
                <input
                  id="broadcaster-password"
                  className="auth-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter password"
                  disabled={setupState === "loading"}
                />
                <button
                  type="button"
                  className="secondary-button auth-toggle-button"
                  onClick={() => setShowPassword((value) => !value)}
                  disabled={setupState === "loading"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <button
                className="primary-button auth-button"
                type="submit"
                disabled={setupState === "loading"}
              >
                {setupState === "loading"
                  ? "Preparing..."
                  : "Unlock broadcaster"}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function ViewerScreen({
  stageArn,
  viewerToken,
  viewerJoinUrl,
  roomId,
  instId,
  userId,
  embed,
  chatApi,
  hideEverything,
  portraitMode,
}) {
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const remoteMediaStreamRef = useRef(null);
  const [viewerState, setViewerState] = useState("loading");
  const [viewerError, setViewerError] = useState("");
  const [viewerHint, setViewerHint] = useState(
    "Connecting to the live stage...",
  );
  const [needsManualStart, setNeedsManualStart] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const chatRoomId = buildChatRoomId(roomId, stageArn, viewerJoinUrl);
  const { viewerCount, presenceReady } = usePresence({
    roomId: chatRoomId,
    role: "viewer",
    apiBase: chatApi,
  });

  useEffect(() => {
    document.body.classList.add("viewer-no-scroll");

    return () => {
      document.body.classList.remove("viewer-no-scroll");
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    const remoteMediaStream = new MediaStream();
    remoteMediaStreamRef.current = remoteMediaStream;
    video.srcObject = remoteMediaStream;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;

    const tryStartPlayback = async () => {
      try {
        await video.play();
        setViewerState("playing");
        setViewerHint("Real-time playback started.");
        setNeedsManualStart(false);
      } catch (error) {
        setViewerState("ready");
        setViewerHint("Tap play if your browser blocked autoplay.");
        setNeedsManualStart(true);
      }
    };

    const handleLoadedMetadata = () => {
      void tryStartPlayback();
    };

    const handlePlaying = () => {
      setViewerState("playing");
      setViewerHint("Real-time playback started.");
      setNeedsManualStart(false);
    };

    const handleVideoError = () => {
      setViewerState("error");
      setViewerError("The video element could not play this live stream.");
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("error", handleVideoError);

    let isMounted = true;

    const joinStage = async () => {
      try {
        const resolvedToken = await resolveViewerParticipantToken({
          viewerToken,
          viewerJoinUrl,
          stageArn,
          instId,
          userId,
        });

        if (!isMounted) {
          return;
        }

        const stage = new Stage(resolvedToken, {
          stageStreamsToPublish() {
            return [];
          },
          shouldPublishParticipant() {
            return false;
          },
          shouldSubscribeToParticipant() {
            return SubscribeType.AUDIO_VIDEO;
          },
        });

        stageRef.current = stage;
        stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (nextState) => {
          if (!isMounted) {
            return;
          }

          setViewerState(nextState);

          if (nextState === StageConnectionState.CONNECTED) {
            setViewerHint("Connected to the real-time stage.");
          } else if (nextState === StageConnectionState.CONNECTING) {
            setViewerHint("Joining the real-time stage...");
          } else if (nextState === StageConnectionState.DISCONNECTED) {
            setViewerHint("Disconnected from the real-time stage.");
          }
        });
        stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (_, streams) => {
          streams.forEach((stream) => {
            remoteMediaStream.addTrack(stream.mediaStreamTrack);
          });

          if (isMounted) {
            setViewerHint("Receiving real-time media.");
            void tryStartPlayback();
          }
        });
        stage.on(
          StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED,
          (_, streams) => {
            streams.forEach((stream) => {
              remoteMediaStream
                .getTracks()
                .filter((track) => track.id === stream.mediaStreamTrack.id)
                .forEach((track) => remoteMediaStream.removeTrack(track));
            });
          },
        );
        stage.on(StageEvents.ERROR, (error) => {
          if (!isMounted) {
            return;
          }

          setViewerState("error");
          setViewerError(
            error?.message || "Unable to join the real-time stage.",
          );
        });
        stage.on(StageEvents.STAGE_LEFT, () => {
          if (!isMounted) {
            return;
          }

          setViewerState("ended");
          setViewerHint("The broadcaster ended this live session.");
          setViewerError("This live has ended.");
        });

        await stage.join();
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setViewerState("error");
        setViewerError(
          error?.message || "Unable to connect to the real-time stage.",
        );
      }
    };

    void joinStage();

    return () => {
      isMounted = false;

      if (stageRef.current) {
        stageRef.current.leave();
      }

      stageRef.current = null;
      video.pause();
      video.srcObject = null;
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("error", handleVideoError);
    };
  }, [viewerToken, viewerJoinUrl, stageArn, instId, userId]);

  const startManually = async () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    try {
      await video.play();
      setViewerState("playing");
      setViewerHint("Real-time playback started.");
      setNeedsManualStart(false);
    } catch (error) {
      setViewerState("error");
      setViewerError(
        "The browser still blocked playback. Try using the native controls.",
      );
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const shellClassName = [
    "viewer-shell",
    embed ? "viewer-shell-embed" : "",
    hideEverything ? "viewer-shell-minimal" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const panelClassName = [
    "viewer-panel",
    embed ? "viewer-panel-embed" : "",
    hideEverything ? "viewer-panel-minimal" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const videoCardClassName = [
    "viewer-video-card",
    embed ? "viewer-video-card-embed" : "",
    portraitMode ? "viewer-video-card-portrait" : "",
    hideEverything ? "viewer-video-card-minimal" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const videoClassName = [
    "viewer-video",
    portraitMode ? "viewer-video-portrait" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className={shellClassName}>
      <section className={panelClassName}>
        <div className={videoCardClassName}>
          <video
            ref={videoRef}
            className={videoClassName}
            controls
            autoPlay
            playsInline
            muted={isMuted}
          />
          {!hideEverything ? (
            <div className="viewer-overlay">
              <div className="viewer-overlay-top">
                {!embed ? (
                  <div className="viewer-top-stack">
                    <p className="eyebrow">Viewer Screen</p>
                    <h1 className="viewer-screen-title">
                      Watch the real-time broadcast.
                    </h1>
                  </div>
                ) : null}
                <span className="viewer-live-pill">Live</span>
                <span className="viewer-state-pill">{viewerState}</span>
                <span className="viewer-state-pill">
                  {presenceReady ? `${viewerCount} viewers` : "-- viewers"}
                </span>
              </div>
              <div className="viewer-overlay-bottom">
                <p className="viewer-hint">{viewerHint}</p>
                <div className="viewer-overlay-actions">
                  {needsManualStart ? (
                    <button className="primary-button" onClick={startManually}>
                      Tap to play
                    </button>
                  ) : null}
                  <button className="secondary-button" onClick={toggleMute}>
                    {isMuted ? "Unmute" : "Mute"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {!hideEverything && viewerError ? (
            <div className="viewer-alert-overlay">
              <p className="error-banner viewer-error-banner">{viewerError}</p>
            </div>
          ) : null}

          {!hideEverything ? (
            <ChatRoom
              roomId={chatRoomId}
              role="viewer"
              compact
              embed={embed}
              overlay
              apiBase={chatApi}
            />
          ) : null}
        </div>
      </section>
    </main>
  );
}

function App() {
  const viewerParams = getViewerParams();
  const hasRequiredParams = Boolean(viewerParams.instId && viewerParams.userId);

  if (!hasRequiredParams) {
    return <MissingParamsOverlay />;
  }

  if (viewerParams.role === "viewer") {
    return (
      <ViewerScreen
        stageArn={viewerParams.stageArn}
        viewerToken={viewerParams.viewerToken}
        viewerJoinUrl={viewerParams.viewerJoinUrl}
        roomId={viewerParams.roomId}
        instId={viewerParams.instId}
        userId={viewerParams.userId}
        embed={viewerParams.embed}
        chatApi={viewerParams.chatApi}
        hideEverything={viewerParams.hideEverything}
        portraitMode={viewerParams.portraitMode}
      />
    );
  }

  return (
    <BroadcasterScreen
      instId={viewerParams.instId}
      userId={viewerParams.userId}
    />
  );
}

export default App;
